// Reliability + Audits seed for the retail-chain demo scenario.
// Gives the Issues inbox, Locations catalog, and Audits screens realistic content
// out of the box: a published audit with questions + run history (so Results and
// Statistics populate), eight physical store locations (matching the "8 Meridian
// locations" the onboarding copy elsewhere in this file already claims - only the
// first four have run history, the rest exercise the "not run yet" / >5-locations
// search-filter states), and issues spanning every source (overdue, missed, can't-
// complete flag, manual report, audit finding) plus a resolved/dismissed history so
// "My reports" and the resolved tab aren't empty.
//
// IDs use stable, unique prefixes. References to task IDs match retail-chain.ts.

import {TENANT_ID, WORKSPACE_ID, CLIENT_USER_ID, CLIENT_USER_ID_2, CLIENT_USER_ID_3, ROLE_ID_STORE_MANAGER, ROLE_ID_FLOOR_ASSOCIATE} from '../fixtures';
import {TLU_DAILY, TLU_INVENTORY} from './retail-chain';

// Task IDs referenced by issues (mirror the literals in retail-chain.ts).
const TASK_CASH_DROP = 'ta7001-0000-0000-0000-000000000087';
const TASK_PHOTO_STOCKROOM = 'ta8001-0000-0000-0000-000000000088';
const TASK_ENTRANCE_SIGN = 'tae001-0000-0000-0000-00000000008e';
const TASK_PULL_STOCK = 'taa001-0000-0000-0000-00000000008a';
const TASK_PHOTO_SHELVES = 'tab001-0000-0000-0000-00000000008b';
const TASK_SHELF_AUDIT = 'tac001-0000-0000-0000-00000000008c';

// ── Locations ─────────────────────────────────────────────────────────────────
export const LOC_DOWNTOWN = 'loc001-0000-0000-0000-0000000000a1';
export const LOC_WESTFIELD = 'loc002-0000-0000-0000-0000000000a2';
export const LOC_HARBOR = 'loc003-0000-0000-0000-0000000000a3';
export const LOC_AIRPORT = 'loc004-0000-0000-0000-0000000000a4';
export const LOC_NORTHLOOP = 'loc005-0000-0000-0000-0000000000a5';
export const LOC_RIVERSIDE = 'loc006-0000-0000-0000-0000000000a6';
export const LOC_SOUTHGATE = 'loc007-0000-0000-0000-0000000000a7';
export const LOC_UNIVERSITY = 'loc008-0000-0000-0000-0000000000a8';

function loc(id: string, name: string, address: string, createdAt: string) {
    return {
        __typename: 'Location' as const,
        id, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        name, address, archived: false,
        version: 1, createdAt, updatedAt: createdAt,
        createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID, deletedAt: null,
    };
}

export const locations = [
    loc(LOC_DOWNTOWN,   'Downtown Flagship',   '142 Market St, Chicago, IL',      '2026-01-02T09:00:00.000Z'),
    loc(LOC_WESTFIELD,  'Westfield Mall',      '88 Westfield Ave, Oak Park, IL',  '2026-01-02T09:05:00.000Z'),
    loc(LOC_HARBOR,     'Harbor Point',        '7 Lakeshore Dr, Chicago, IL',     '2026-01-02T09:10:00.000Z'),
    loc(LOC_AIRPORT,    'Airport Terminal 3',  'ORD Terminal 3, Chicago, IL',     '2026-01-02T09:15:00.000Z'),
    loc(LOC_NORTHLOOP,  'North Loop',          '210 Wells St, Chicago, IL',       '2026-01-02T09:20:00.000Z'),
    loc(LOC_RIVERSIDE,  'Riverside Plaza',     '35 Riverside Plz, Chicago, IL',   '2026-01-02T09:25:00.000Z'),
    loc(LOC_SOUTHGATE,  'Southgate Outlet',    '900 Southgate Rd, Chicago, IL',   '2026-01-02T09:30:00.000Z'),
    loc(LOC_UNIVERSITY, 'University District', '58 Campus Ave, Evanston, IL',     '2026-01-02T09:35:00.000Z'),
];

// ── Audit template + questions ──────────────────────────────────────────────────
export const AUD_FLOOR = 'audt01-0000-0000-0000-0000000000b1';
export const AUD_ENTRANCE_ZONE = 'audt02-0000-0000-0000-0000000000b2';
export const AUD_CASH_DISCIPLINE = 'audt03-0000-0000-0000-0000000000b3';
export const AUD_FRIDGE_TEMP = 'audt04-0000-0000-0000-0000000000b4';
export const AUD_FIRE_SAFETY = 'audt05-0000-0000-0000-0000000000b5';
export const AUD_GOODS_RECEIVING = 'audt06-0000-0000-0000-0000000000b6';

const AQ_ENTRANCE = 'audq01-0000-0000-0000-0000000000c1';
const AQ_SHELVES  = 'audq02-0000-0000-0000-0000000000c2';
const AQ_TICKETS  = 'audq03-0000-0000-0000-0000000000c3';
const AQ_SIGNAGE  = 'audq04-0000-0000-0000-0000000000c4';
const AQ_SAFETY   = 'audq05-0000-0000-0000-0000000000c5';

// Extra demo audit templates — additional rows for the Audits list table.
const AQ_ENTRANCE_1 = 'audq06-0000-0000-0000-0000000000c6';
const AQ_ENTRANCE_2 = 'audq07-0000-0000-0000-0000000000c7';
const AQ_ENTRANCE_3 = 'audq08-0000-0000-0000-0000000000c8';
const AQ_CASH_1 = 'audq09-0000-0000-0000-0000000000c9';
const AQ_CASH_2 = 'audq10-0000-0000-0000-0000000000ca';
const AQ_CASH_3 = 'audq11-0000-0000-0000-0000000000cb';
const AQ_CASH_4 = 'audq12-0000-0000-0000-0000000000cc';
const AQ_FRIDGE_1 = 'audq13-0000-0000-0000-0000000000cd';
const AQ_FRIDGE_2 = 'audq14-0000-0000-0000-0000000000ce';
const AQ_FIRE_1 = 'audq15-0000-0000-0000-0000000000cf';
const AQ_FIRE_2 = 'audq16-0000-0000-0000-0000000000d0';
const AQ_FIRE_3 = 'audq17-0000-0000-0000-0000000000d1';
const AQ_FIRE_4 = 'audq18-0000-0000-0000-0000000000d2';
const AQ_FIRE_5 = 'audq19-0000-0000-0000-0000000000d3';
const AQ_RECV_1 = 'audq20-0000-0000-0000-0000000000d4';
const AQ_RECV_2 = 'audq21-0000-0000-0000-0000000000d5';
const AQ_RECV_3 = 'audq22-0000-0000-0000-0000000000d6';

// Store Floor & Safety Audit — extra questions (5 → 25) so the Answer stats
// "By location" matrix has real width to demonstrate against (7 of 8 locations
// have run history). See the auditQuestions block below for point values/flags.
const AQ_FLOOR_AISLES = 'audq23-0000-0000-0000-000000000101';
const AQ_FLOOR_FITTING = 'audq24-0000-0000-0000-000000000102';
const AQ_FLOOR_RESTROOMS = 'audq25-0000-0000-0000-000000000103';
const AQ_FLOOR_BACKROOM = 'audq26-0000-0000-0000-000000000104';
const AQ_FLOOR_REGISTER = 'audq27-0000-0000-0000-000000000105';
const AQ_FLOOR_DIGITAL_PRICE = 'audq28-0000-0000-0000-000000000106';
const AQ_FLOOR_ACCESS = 'audq29-0000-0000-0000-000000000107';
const AQ_FLOOR_EXTINGUISHER = 'audq30-0000-0000-0000-000000000108';
const AQ_FLOOR_EMERGENCY_LIGHT = 'audq31-0000-0000-0000-000000000109';
const AQ_FLOOR_UNIFORM = 'audq32-0000-0000-0000-000000000110';
const AQ_FLOOR_CARTS = 'audq33-0000-0000-0000-000000000111';
const AQ_FLOOR_LOYALTY = 'audq34-0000-0000-0000-000000000112';
const AQ_FLOOR_WINDOW = 'audq35-0000-0000-0000-000000000113';
const AQ_FLOOR_TEMP = 'audq36-0000-0000-0000-000000000114';
const AQ_FLOOR_MUSIC = 'audq37-0000-0000-0000-000000000115';
const AQ_FLOOR_CAMERAS = 'audq38-0000-0000-0000-000000000116';
const AQ_FLOOR_WASTE = 'audq39-0000-0000-0000-000000000117';
const AQ_FLOOR_PARKING_LIGHT = 'audq40-0000-0000-0000-000000000118';
const AQ_FLOOR_BREAKROOM = 'audq41-0000-0000-0000-000000000119';
const AQ_FLOOR_PLANOGRAM = 'audq42-0000-0000-0000-000000000120';

const AUDIT_NOW = '2026-06-01T10:00:00.000Z';

export const auditTemplates = [
    {
        __typename: 'AuditTemplate' as const,
        id: AUD_FLOOR, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        name: 'Store Floor & Safety Audit',
        description: 'Monthly walk-through scoring floor presentation, pricing accuracy, and safety compliance. Run at each location.',
        requiredPassPercentage: 80,
        publicationState: 'published',
        questionsCount: 25,
        questionsOrder: [
            AQ_ENTRANCE, AQ_SHELVES, AQ_TICKETS, AQ_SIGNAGE, AQ_SAFETY,
            AQ_FLOOR_AISLES, AQ_FLOOR_FITTING, AQ_FLOOR_RESTROOMS, AQ_FLOOR_BACKROOM, AQ_FLOOR_REGISTER,
            AQ_FLOOR_DIGITAL_PRICE, AQ_FLOOR_ACCESS, AQ_FLOOR_EXTINGUISHER, AQ_FLOOR_EMERGENCY_LIGHT, AQ_FLOOR_UNIFORM,
            AQ_FLOOR_CARTS, AQ_FLOOR_LOYALTY, AQ_FLOOR_WINDOW, AQ_FLOOR_TEMP, AQ_FLOOR_MUSIC,
            AQ_FLOOR_CAMERAS, AQ_FLOOR_WASTE, AQ_FLOOR_PARKING_LIGHT, AQ_FLOOR_BREAKROOM, AQ_FLOOR_PLANOGRAM,
        ],
        version: 1, createdAt: AUDIT_NOW, updatedAt: AUDIT_NOW,
        createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID, deletedAt: null,
    },
    {
        __typename: 'AuditTemplate' as const,
        id: AUD_ENTRANCE_ZONE, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        name: 'Чистота входной зоны',
        description: 'Ежедневная проверка входной группы: пол, витрины, коврики и таблички у входа.',
        requiredPassPercentage: 80,
        publicationState: 'published',
        questionsCount: 3,
        questionsOrder: [AQ_ENTRANCE_1, AQ_ENTRANCE_2, AQ_ENTRANCE_3],
        version: 1, createdAt: AUDIT_NOW, updatedAt: AUDIT_NOW,
        createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID, deletedAt: null,
    },
    {
        __typename: 'AuditTemplate' as const,
        id: AUD_CASH_DISCIPLINE, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        name: 'Кассовая дисциплина',
        description: 'Еженедельная проверка соблюдения кассовых процедур и сверки смены.',
        requiredPassPercentage: 80,
        publicationState: 'published',
        questionsCount: 4,
        questionsOrder: [AQ_CASH_1, AQ_CASH_2, AQ_CASH_3, AQ_CASH_4],
        version: 1, createdAt: AUDIT_NOW, updatedAt: AUDIT_NOW,
        createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID, deletedAt: null,
    },
    {
        __typename: 'AuditTemplate' as const,
        id: AUD_FRIDGE_TEMP, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        name: 'Температура холодильников',
        description: 'Контроль температурного режима холодильного и морозильного оборудования.',
        requiredPassPercentage: 80,
        publicationState: 'draft',
        questionsCount: 2,
        questionsOrder: [AQ_FRIDGE_1, AQ_FRIDGE_2],
        version: 1, createdAt: AUDIT_NOW, updatedAt: AUDIT_NOW,
        createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID, deletedAt: null,
    },
    {
        __typename: 'AuditTemplate' as const,
        id: AUD_FIRE_SAFETY, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        name: 'Пожарная безопасность',
        description: 'Ежемесячная проверка пожарных выходов, огнетушителей и путей эвакуации.',
        requiredPassPercentage: 80,
        publicationState: 'published',
        questionsCount: 5,
        questionsOrder: [AQ_FIRE_1, AQ_FIRE_2, AQ_FIRE_3, AQ_FIRE_4, AQ_FIRE_5],
        version: 1, createdAt: AUDIT_NOW, updatedAt: AUDIT_NOW,
        createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID, deletedAt: null,
    },
    {
        __typename: 'AuditTemplate' as const,
        id: AUD_GOODS_RECEIVING, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        name: 'Приёмка товара',
        description: 'Проверка соблюдения процедуры приёмки: сверка накладных, осмотр упаковки, маркировка.',
        requiredPassPercentage: 80,
        publicationState: 'draft',
        questionsCount: 3,
        questionsOrder: [AQ_RECV_1, AQ_RECV_2, AQ_RECV_3],
        version: 1, createdAt: AUDIT_NOW, updatedAt: AUDIT_NOW,
        createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID, deletedAt: null,
    },
];

function question(id: string, text: string, points: number, photoRequiredToPass: boolean, reference?: any, isCritical?: boolean, allowNA?: boolean) {
    return questionOf(AUD_FLOOR, id, text, points, photoRequiredToPass, reference, isCritical, allowNA);
}

function questionOf(auditTemplateId: string, id: string, text: string, points: number, photoRequiredToPass: boolean, reference?: any, isCritical?: boolean, allowNA?: boolean) {
    return {
        __typename: 'AuditQuestion' as const,
        id, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID, auditTemplateId,
        text, points, photoRequiredToPass, isCritical: isCritical ?? false, allowNA: allowNA ?? false, reference: reference ?? null,
        version: 1, createdAt: AUDIT_NOW, updatedAt: AUDIT_NOW, deletedAt: null,
    };
}

export const auditQuestions = [
    question(AQ_ENTRANCE, 'Is the entrance clean and unobstructed?', 2, false,
        {description: 'Doorway, mats, and the first 3 metres of floor should be clean and free of stock or signage clutter.'}),
    question(AQ_SHELVES, 'Are shelves fully stocked to the face?', 2, false,
        {kbLink: 'lfb001-0000-0000-0000-00000000005b'}),
    question(AQ_TICKETS, 'Are all price tickets visible and correct?', 2, false),
    // allowNA: true — not every visit falls during an active promo, so "matches
    // the current campaign" doesn't always apply.
    question(AQ_SIGNAGE, 'Does promotional signage match the current campaign?', 1, false, undefined, undefined, true),
    // photoRequiredToPass — the most safety-critical check needs photo evidence to pass.
    // isCritical: true — a "No" here auto-fails the audit regardless of overall score
    // (see the Results tab's critical-fail badge and the "Critical fails" KPI).
    question(AQ_SAFETY, 'Are all safety standards met (fire exits clear, wet-floor signs out, first-aid kit stocked)?', 3, true, undefined, true),

    // Store Floor & Safety Audit — extra questions, added so this template has
    // enough rows (25) to make the Answer stats "By location" matrix worth
    // looking at. Max score for this block: 18×2 + 3 + 1 = 40.
    question(AQ_FLOOR_AISLES, 'Are aisles clear of trip hazards and clutter?', 2, false),
    question(AQ_FLOOR_FITTING, 'Are fitting rooms clean, stocked, and doors functioning?', 2, false),
    question(AQ_FLOOR_RESTROOMS, 'Are restrooms clean and fully stocked?', 2, false),
    // photoRequiredToPass — stockroom organization is easy to fudge without evidence.
    question(AQ_FLOOR_BACKROOM, 'Is the backroom organized with clear aisle labeling?', 2, true),
    question(AQ_FLOOR_REGISTER, 'Is the register area organized and free of clutter?', 2, false),
    question(AQ_FLOOR_DIGITAL_PRICE, 'Are digital price displays functioning correctly?', 2, false),
    // isCritical: true — blocked accessibility is a compliance risk, auto-fails the audit.
    question(AQ_FLOOR_ACCESS, 'Is handicap access (ramps, aisles, parking) unobstructed?', 3, false, undefined, true),
    question(AQ_FLOOR_EXTINGUISHER, 'Is the fire extinguisher tagged, charged, and accessible?', 2, false),
    question(AQ_FLOOR_EMERGENCY_LIGHT, 'Was emergency lighting tested and confirmed functional?', 2, false),
    question(AQ_FLOOR_UNIFORM, 'Is staff wearing name tags and the correct uniform?', 2, false),
    question(AQ_FLOOR_CARTS, 'Are shopping carts and baskets clean and available?', 2, false),
    // allowNA: true — not every location runs a loyalty program display.
    question(AQ_FLOOR_LOYALTY, 'Are loyalty program materials displayed at checkout?', 1, false, undefined, undefined, true),
    question(AQ_FLOOR_WINDOW, 'Are window displays clean, current, and free of damage?', 2, false),
    question(AQ_FLOOR_TEMP, 'Is the store temperature comfortable throughout?', 2, false),
    question(AQ_FLOOR_MUSIC, 'Is in-store music/PA volume at an appropriate level?', 2, false),
    question(AQ_FLOOR_CAMERAS, 'Are loss-prevention cameras operational and unobstructed?', 2, false),
    question(AQ_FLOOR_WASTE, 'Are waste and recycling bins emptied and properly sorted?', 2, false),
    question(AQ_FLOOR_PARKING_LIGHT, 'Is parking lot and entrance lighting functional after dark?', 2, false),
    question(AQ_FLOOR_BREAKROOM, 'Is the break room compliant (clean, schedules posted, exits clear)?', 2, false),
    question(AQ_FLOOR_PLANOGRAM, 'Do endcaps match the current merchandising planogram?', 2, false),

    // Чистота входной зоны — max score 2+2+1 = 5.
    questionOf(AUD_ENTRANCE_ZONE, AQ_ENTRANCE_1, 'Пол у входа чистый, без мусора и разводов?', 2, false),
    questionOf(AUD_ENTRANCE_ZONE, AQ_ENTRANCE_2, 'Коврики на месте и не скользят?', 2, false),
    questionOf(AUD_ENTRANCE_ZONE, AQ_ENTRANCE_3, 'Витрина у входа оформлена по текущей акции?', 1, false),

    // Кассовая дисциплина — max score 2+2+2+1 = 7. Deliberately spans the taking
    // screen's different guidance/answer states on one audit (matches what's
    // already live under AUD_CASH_DISCIPLINE): a fully-filled guide (description +
    // reference photo + KB link) on Cash 1, KB-link-only guide, the
    // photoRequiredToPass criterion, and an allowNA question.
    // Fully-filled Guidance example: description + reference photo ("how it should
    // look") + linked KB page, all three at once — see AuditGuidanceBlock.
    questionOf(AUD_CASH_DISCIPLINE, AQ_CASH_1, 'Кассир идентифицирован (бейдж, форма)?', 2, false, {
        description: 'Бейдж должен быть закреплён на форме и хорошо виден покупателю; форма — по актуальному дресс-коду смены.',
        // No `path` here (unlike answer photos below) — ImageGalleryPreview's
        // singleFull mode (used for the Guidance reference photo) prefers
        // resolving `path` via the fileProvider over `src` when both are set,
        // which would break this offline data-URI placeholder.
        photos: [{src: demoEvidencePhoto('Бейдж на форме — эталон', '#2e9e5b')}],
        // Customer Service Standards (LEAF_CUST_SVC in retail-chain.ts — not
        // imported by id since that file doesn't export its leaf-page constants).
        kbLink: 'lf3001-0000-0000-0000-000000000053',
    }),
    // kbLink reuses POS & Cash Handling (LEAF_POS in retail-chain.ts) — not
    // imported by id since that file doesn't export its leaf-page constants.
    questionOf(AUD_CASH_DISCIPLINE, AQ_CASH_2, 'Сверка наличности при открытии смены проведена?', 2, false,
        {kbLink: 'lf2001-0000-0000-0000-000000000052'}),
    // isCritical: true — возвраты без подписи менеджера — риск мошенничества,
    // отмечаем как критичный пункт независимо от общего балла по прогону.
    // photoRequiredToPass: true — the "Attach a photo before you can answer" criterion.
    questionOf(AUD_CASH_DISCIPLINE, AQ_CASH_3, 'Возвраты и отмены оформлены с подписью менеджера?', 2, true, {
        description: 'На чеке возврата должна стоять подпись ответственного менеджера смены.',
        photos: [{src: demoEvidencePhoto('Чек с подписью менеджера — эталон', '#2e9e5b')}],
    }, true),
    // allowNA: true — some shifts run self-checkout only, so "issued to every
    // customer" doesn't always apply; N/A bypasses the (non-existent, here)
    // photo gate the way AuditTaking.tsx's selectNA comment describes.
    questionOf(AUD_CASH_DISCIPLINE, AQ_CASH_4, 'Чек выдаётся каждому покупателю?', 1, false, undefined, undefined, true),

    // Температура холодильников — max score 1+1 = 2.
    questionOf(AUD_FRIDGE_TEMP, AQ_FRIDGE_1, 'Температура в холодильных витринах в норме (0–5°C)?', 1, false),
    questionOf(AUD_FRIDGE_TEMP, AQ_FRIDGE_2, 'Температура в морозильных камерах в норме (ниже -18°C)?', 1, false),

    // Пожарная безопасность — max score 2+2+2+2+2 = 10.
    questionOf(AUD_FIRE_SAFETY, AQ_FIRE_1, 'Пути эвакуации свободны от препятствий?', 2, false),
    questionOf(AUD_FIRE_SAFETY, AQ_FIRE_2, 'Огнетушители на месте и не просрочены?', 2, true),
    questionOf(AUD_FIRE_SAFETY, AQ_FIRE_3, 'Аварийные выходы не заблокированы и легко открываются?', 2, false),
    questionOf(AUD_FIRE_SAFETY, AQ_FIRE_4, 'Указатели эвакуации исправны и хорошо видны?', 2, false),
    questionOf(AUD_FIRE_SAFETY, AQ_FIRE_5, 'Персонал знает план эвакуации и место сбора?', 2, false),

    // Приёмка товара — max score 2+1+1 = 4.
    questionOf(AUD_GOODS_RECEIVING, AQ_RECV_1, 'Накладная сверена с фактическим количеством товара?', 2, false),
    questionOf(AUD_GOODS_RECEIVING, AQ_RECV_2, 'Упаковка осмотрена на предмет повреждений?', 1, false),
    questionOf(AUD_GOODS_RECEIVING, AQ_RECV_3, 'Товар промаркирован и размещён на складе в тот же день?', 1, false),
];
// Max score (Store Floor & Safety Audit): 10 (original 5) + 40 (extra 20) = 50.

// ── Audit runs (completed history → drives Results + Statistics) ─────────────────
function runOf(
    id: string, auditTemplateId: string, locationId: string, score: number, maxScore: number, completedAt: string,
    auditorId: string, startedAt: string = completedAt,
) {
    const percentage = Math.round((score / maxScore) * 100);
    return {
        __typename: 'AuditRun' as const,
        id, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID, auditTemplateId,
        locationId, auditorId,
        status: 'completed' as const,
        score, maxScore, percentage, passed: percentage >= 80,
        startedAt, completedAt,
        version: 1, createdAt: completedAt, updatedAt: completedAt, deletedAt: null,
    };
}

const RUN_DOWNTOWN = 'audr01-0000-0000-0000-0000000000d1';
const RUN_WESTFIELD = 'audr02-0000-0000-0000-0000000000d2';
const RUN_HARBOR = 'audr03-0000-0000-0000-0000000000d3'; // failing run → audit_finding issue
const RUN_ENTRANCE_ZONE = 'audr04-0000-0000-0000-0000000000d7';
const RUN_CASH_DISCIPLINE = 'audr05-0000-0000-0000-0000000000d8';
const RUN_FIRE_SAFETY = 'audr06-0000-0000-0000-0000000000d9';

const baseAuditRuns = [
    // Store Floor & Safety Audit runs for Downtown/Westfield/Harbor now live in
    // extraRunEntries below (makeRun) since they need per-question answers across
    // all 25 questions — see the "Store Floor & Safety Audit" block there.

    // Чистота входной зоны — Downtown, 4/5 (80%) → passed.
    runOf(RUN_ENTRANCE_ZONE, AUD_ENTRANCE_ZONE, LOC_DOWNTOWN, 4, 5, '2026-06-22T08:15:00.000Z', CLIENT_USER_ID),
    // Кассовая дисциплина — Westfield, 3/7 (43%) → failed on 2 questions. Demo scenario
    // for multiple manually-created Issues on one result (vs. Harbor Point above, which
    // stays at exactly one) — see the two audit_finding issues below.
    // Runs 17:40–17:52 UTC (20:40–20:52 MSK) — a real span, so the summary card's
    // start/end range and each card's answer time are visibly distinct.
    runOf(RUN_CASH_DISCIPLINE, AUD_CASH_DISCIPLINE, LOC_WESTFIELD, 3, 7, '2026-06-23T17:52:00.000Z', CLIENT_USER_ID_2, '2026-06-23T17:40:00.000Z'),
    // Пожарная безопасность — Airport Terminal 3, 10/10 (100%) → passed.
    runOf(RUN_FIRE_SAFETY, AUD_FIRE_SAFETY, LOC_AIRPORT, 10, 10, '2026-06-24T09:00:00.000Z', CLIENT_USER_ID),

    // Температура холодильников and Приёмка товара are still drafts — never run yet.
];

// Inline SVG placeholder for audit-evidence photos — fully offline, no server
// file needed. Same trick as DEMO_FLOOR_IMG in retail-chain.ts: ImageGalleryPreview
// falls through to `src` when `thumbPath` is absent, so this renders directly.
function demoEvidencePhoto(caption: string, accent = '#4f7bf5') {
    const svg =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480">' +
        '<rect width="640" height="480" fill="#eceff5"/>' +
        `<circle cx="320" cy="210" r="90" fill="${accent}" opacity="0.12"/>` +
        `<path d="M256 184h28l14-22h44l14 22h28a14 14 0 0 1 14 14v104a14 14 0 0 1-14 14H256a14 14 0 0 1-14-14V198a14 14 0 0 1 14-14z" fill="${accent}" opacity="0.55"/>` +
        `<circle cx="334" cy="246" r="34" fill="#eceff5"/>` +
        `<circle cx="334" cy="246" r="24" fill="${accent}" opacity="0.6"/>` +
        '<rect y="432" width="640" height="48" fill="#00000066"/>' +
        `<text x="320" y="461" font-family="Arial,sans-serif" font-size="14" fill="#fff" text-anchor="middle">${caption}</text>` +
        '</svg>';
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function answer(
    id: string, runId: string, questionId: string, isYes: boolean, score: number, completedAt: string,
    comment: string | null = null, photoCaptions: string[] | null = null,
) {
    return {
        __typename: 'AuditAnswer' as const,
        id, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        auditRunId: runId, auditQuestionId: questionId,
        answer: isYes, score, comment,
        photos: photoCaptions ? photoCaptions.map((caption, i) => ({
            src: demoEvidencePhoto(caption, isYes ? '#2e9e5b' : '#d34c4c'),
            path: `audits/${id}-${i + 1}.jpg`,
        })) : null,
        createdAt: completedAt, updatedAt: completedAt,
    };
}

const baseAuditAnswers = [
    // Westfield — Кассовая дисциплина, 3/7 (fails Сверка наличности + Возвраты — the two questions the admin manually raised issues from)
    answer('ans-cd-1', RUN_CASH_DISCIPLINE, AQ_CASH_1, true,  2, '2026-06-23T17:41:00.000Z',
        'Кассир Мария И. — в форме, бейдж на месте, хорошо виден.',
        ['Кассир — бейдж и форма']),
    answer('ans-cd-2', RUN_CASH_DISCIPLINE, AQ_CASH_2, false, 0, '2026-06-23T17:44:00.000Z',
        'Акт сверки наличности на начало смены не предоставлен — только устное подтверждение кассира.'),
    // Three photos (not two) — demonstrates the Issue triage drawer's Fact-side
    // mini-gallery (one large + the rest as thumbnails) when several are attached.
    answer('ans-cd-3', RUN_CASH_DISCIPLINE, AQ_CASH_3, false, 0, '2026-06-23T17:48:00.000Z',
        'Два возврата за смену оформлены без подписи ответственного менеджера на чеке.',
        ['Чек возврата №1 — без подписи', 'Чек возврата №2 — без подписи', 'Журнал возвратов за смену']),
    answer('ans-cd-4', RUN_CASH_DISCIPLINE, AQ_CASH_4, true,  1, '2026-06-23T17:52:00.000Z',
        'Проверено на 5 транзакциях подряд — чек выдан покупателю каждый раз.',
        ['Выдача чека покупателю']),
];

// ── Bulk-fill extra runs ──────────────────────────────────────────────────────────
// The Overview table is a full cross-join of published templates × locations
// (4 × 8 = 32 cells), and the handful of runs above only cover 6 of them — the rest
// read as "No score yet", which makes the page look empty. Fill most of the
// remaining cells with a completed run (+ matching per-question answers) so the
// table reads like an active audit program. Left intentionally unrun: University
// District on every template (reads as "newly opened, not yet audited"), plus
// Southgate on Чистота входной зоны and North Loop on Кассовая дисциплина — a real
// program never has 100% coverage either.
type AnswerSpec = {questionId: string; points: number; yes: boolean; comment?: string};

function makeRun(id: string, auditTemplateId: string, locationId: string, completedAt: string, auditorId: string, specs: AnswerSpec[]) {
    const maxScore = specs.reduce((sum, s) => sum + s.points, 0);
    const score = specs.reduce((sum, s) => sum + (s.yes ? s.points : 0), 0);
    return {
        run: runOf(id, auditTemplateId, locationId, score, maxScore, completedAt, auditorId),
        answers: specs.map((s, i) => answer(`${id}-a${i + 1}`, id, s.questionId, s.yes, s.yes ? s.points : 0, completedAt, s.comment ?? null)),
    };
}

// Store Floor & Safety Audit — the 20 extra questions (see auditQuestions above),
// factored into a table + helper since 7 locations × 25 questions is too much to
// hand-write as literal specs. floorExtraSpecs marks the given question ids as
// failed and leaves the rest passing, matching the makeRun/AnswerSpec shape.
const FLOOR_EXTRA_QUESTIONS: {id: string; points: number}[] = [
    {id: AQ_FLOOR_AISLES, points: 2},
    {id: AQ_FLOOR_FITTING, points: 2},
    {id: AQ_FLOOR_RESTROOMS, points: 2},
    {id: AQ_FLOOR_BACKROOM, points: 2},
    {id: AQ_FLOOR_REGISTER, points: 2},
    {id: AQ_FLOOR_DIGITAL_PRICE, points: 2},
    {id: AQ_FLOOR_ACCESS, points: 3},
    {id: AQ_FLOOR_EXTINGUISHER, points: 2},
    {id: AQ_FLOOR_EMERGENCY_LIGHT, points: 2},
    {id: AQ_FLOOR_UNIFORM, points: 2},
    {id: AQ_FLOOR_CARTS, points: 2},
    {id: AQ_FLOOR_LOYALTY, points: 1},
    {id: AQ_FLOOR_WINDOW, points: 2},
    {id: AQ_FLOOR_TEMP, points: 2},
    {id: AQ_FLOOR_MUSIC, points: 2},
    {id: AQ_FLOOR_CAMERAS, points: 2},
    {id: AQ_FLOOR_WASTE, points: 2},
    {id: AQ_FLOOR_PARKING_LIGHT, points: 2},
    {id: AQ_FLOOR_BREAKROOM, points: 2},
    {id: AQ_FLOOR_PLANOGRAM, points: 2},
]; // max 40

function floorExtraSpecs(failIds: string[], comments: Record<string, string> = {}): AnswerSpec[] {
    const failSet = new Set(failIds);
    return FLOOR_EXTRA_QUESTIONS.map((q) => ({questionId: q.id, points: q.points, yes: !failSet.has(q.id), comment: comments[q.id]}));
}

const extraRunEntries = [
    // Store Floor & Safety Audit — original 5 questions keep their prior fail
    // pattern per location (see comments), extended with the 20 extra questions
    // so every run scales to /50 while landing on ~the same pass percentage.
    // Downtown — 9/10 on the original 5 (fails only Signage) + 45/50 minor misses → 90%, passed.
    makeRun(RUN_DOWNTOWN, AUD_FLOOR, LOC_DOWNTOWN, '2026-06-15T11:30:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_ENTRANCE, points: 2, yes: true},
        {questionId: AQ_SHELVES, points: 2, yes: true},
        {questionId: AQ_TICKETS, points: 2, yes: true},
        {questionId: AQ_SIGNAGE, points: 1, yes: false},
        {questionId: AQ_SAFETY, points: 3, yes: true},
        ...floorExtraSpecs([AQ_FLOOR_DIGITAL_PRICE, AQ_FLOOR_MUSIC]),
    ]),
    // Westfield — 8/10 on the original 5 (fails only Tickets) → 40/50 → 80%, passed.
    makeRun(RUN_WESTFIELD, AUD_FLOOR, LOC_WESTFIELD, '2026-06-16T14:00:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_ENTRANCE, points: 2, yes: true},
        {questionId: AQ_SHELVES, points: 2, yes: true},
        {questionId: AQ_TICKETS, points: 2, yes: false},
        {questionId: AQ_SIGNAGE, points: 1, yes: true},
        {questionId: AQ_SAFETY, points: 3, yes: true},
        ...floorExtraSpecs([AQ_FLOOR_AISLES, AQ_FLOOR_EMERGENCY_LIGHT, AQ_FLOOR_WASTE, AQ_FLOOR_PLANOGRAM]),
    ]),
    // Harbor — 3/10 on the original 5 (fails Shelves + Tickets + Safety, the last
    // one critical) + mostly-failing extras (incl. a second critical fail on
    // Handicap access) → 15/50 → 30%, failed. Drives the audit_finding issue below.
    makeRun(RUN_HARBOR, AUD_FLOOR, LOC_HARBOR, '2026-06-18T10:15:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_ENTRANCE, points: 2, yes: true},
        {questionId: AQ_SHELVES, points: 2, yes: false},
        {questionId: AQ_TICKETS, points: 2, yes: false},
        {questionId: AQ_SIGNAGE, points: 1, yes: true},
        {questionId: AQ_SAFETY, points: 3, yes: false},
        ...floorExtraSpecs([
            AQ_FLOOR_AISLES, AQ_FLOOR_FITTING, AQ_FLOOR_BACKROOM, AQ_FLOOR_REGISTER, AQ_FLOOR_DIGITAL_PRICE,
            AQ_FLOOR_ACCESS, AQ_FLOOR_EXTINGUISHER, AQ_FLOOR_EMERGENCY_LIGHT, AQ_FLOOR_LOYALTY, AQ_FLOOR_MUSIC,
            AQ_FLOOR_CAMERAS, AQ_FLOOR_WASTE, AQ_FLOOR_PARKING_LIGHT, AQ_FLOOR_PLANOGRAM,
        ]),
    ]),
    makeRun('audr07-0000-0000-0000-0000000000da', AUD_FLOOR, LOC_AIRPORT, '2026-06-19T09:30:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_ENTRANCE, points: 2, yes: true},
        {questionId: AQ_SHELVES, points: 2, yes: true},
        {questionId: AQ_TICKETS, points: 2, yes: true},
        {questionId: AQ_SIGNAGE, points: 1, yes: false},
        {questionId: AQ_SAFETY, points: 3, yes: true},
        ...floorExtraSpecs([AQ_FLOOR_EXTINGUISHER, AQ_FLOOR_PARKING_LIGHT]),
    ]),
    makeRun('audr08-0000-0000-0000-0000000000db', AUD_FLOOR, LOC_NORTHLOOP, '2026-06-20T13:00:00.000Z', CLIENT_USER_ID_2, [
        {questionId: AQ_ENTRANCE, points: 2, yes: true},
        {questionId: AQ_SHELVES, points: 2, yes: false, comment: 'Back wall of the snack aisle is half-empty — vendor delivery delayed.'},
        {questionId: AQ_TICKETS, points: 2, yes: true},
        {questionId: AQ_SIGNAGE, points: 1, yes: false},
        {questionId: AQ_SAFETY, points: 3, yes: true},
        ...floorExtraSpecs([AQ_FLOOR_FITTING, AQ_FLOOR_BACKROOM, AQ_FLOOR_EMERGENCY_LIGHT, AQ_FLOOR_CAMERAS, AQ_FLOOR_WASTE, AQ_FLOOR_PLANOGRAM]),
    ]),
    makeRun('audr09-0000-0000-0000-0000000000dc', AUD_FLOOR, LOC_RIVERSIDE, '2026-06-21T10:00:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_ENTRANCE, points: 2, yes: true},
        {questionId: AQ_SHELVES, points: 2, yes: true},
        {questionId: AQ_TICKETS, points: 2, yes: true},
        {questionId: AQ_SIGNAGE, points: 1, yes: true},
        {questionId: AQ_SAFETY, points: 3, yes: true},
        ...floorExtraSpecs([]),
    ]),
    makeRun('audr10-0000-0000-0000-0000000000dd', AUD_FLOOR, LOC_SOUTHGATE, '2026-06-25T15:20:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_ENTRANCE, points: 2, yes: true},
        {questionId: AQ_SHELVES, points: 2, yes: true},
        {questionId: AQ_TICKETS, points: 2, yes: false, comment: "Two clearance racks near the fitting room still show last month's prices."},
        {questionId: AQ_SIGNAGE, points: 1, yes: true},
        {questionId: AQ_SAFETY, points: 3, yes: true},
        ...floorExtraSpecs([AQ_FLOOR_DIGITAL_PRICE, AQ_FLOOR_CARTS, AQ_FLOOR_MUSIC, AQ_FLOOR_BREAKROOM]),
    ]),

    // Чистота входной зоны
    makeRun('audr11-0000-0000-0000-0000000000de', AUD_ENTRANCE_ZONE, LOC_WESTFIELD, '2026-06-11T08:00:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_ENTRANCE_1, points: 2, yes: true},
        {questionId: AQ_ENTRANCE_2, points: 2, yes: true},
        {questionId: AQ_ENTRANCE_3, points: 1, yes: true},
    ]),
    makeRun('audr12-0000-0000-0000-0000000000df', AUD_ENTRANCE_ZONE, LOC_HARBOR, '2026-06-12T08:30:00.000Z', CLIENT_USER_ID_2, [
        {questionId: AQ_ENTRANCE_1, points: 2, yes: true},
        {questionId: AQ_ENTRANCE_2, points: 2, yes: false, comment: 'Входной коврик скручен и скользит на плитке.'},
        {questionId: AQ_ENTRANCE_3, points: 1, yes: true},
    ]),
    makeRun('audr13-0000-0000-0000-0000000000e0', AUD_ENTRANCE_ZONE, LOC_AIRPORT, '2026-06-13T07:45:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_ENTRANCE_1, points: 2, yes: true},
        {questionId: AQ_ENTRANCE_2, points: 2, yes: true},
        {questionId: AQ_ENTRANCE_3, points: 1, yes: false},
    ]),
    makeRun('audr14-0000-0000-0000-0000000000e9', AUD_ENTRANCE_ZONE, LOC_NORTHLOOP, '2026-06-14T08:10:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_ENTRANCE_1, points: 2, yes: true},
        {questionId: AQ_ENTRANCE_2, points: 2, yes: true},
        {questionId: AQ_ENTRANCE_3, points: 1, yes: true},
    ]),
    makeRun('audr15-0000-0000-0000-0000000000ea', AUD_ENTRANCE_ZONE, LOC_RIVERSIDE, '2026-06-17T08:20:00.000Z', CLIENT_USER_ID_2, [
        {questionId: AQ_ENTRANCE_1, points: 2, yes: true},
        {questionId: AQ_ENTRANCE_2, points: 2, yes: true},
        {questionId: AQ_ENTRANCE_3, points: 1, yes: false},
    ]),

    // Кассовая дисциплина
    makeRun('audr16-0000-0000-0000-0000000000eb', AUD_CASH_DISCIPLINE, LOC_DOWNTOWN, '2026-06-09T16:00:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_CASH_1, points: 2, yes: true},
        {questionId: AQ_CASH_2, points: 2, yes: true},
        {questionId: AQ_CASH_3, points: 2, yes: true},
        {questionId: AQ_CASH_4, points: 1, yes: true},
    ]),
    makeRun('audr17-0000-0000-0000-0000000000ec', AUD_CASH_DISCIPLINE, LOC_HARBOR, '2026-06-10T17:10:00.000Z', CLIENT_USER_ID_2, [
        {questionId: AQ_CASH_1, points: 2, yes: true},
        {questionId: AQ_CASH_2, points: 2, yes: true},
        {questionId: AQ_CASH_3, points: 2, yes: false, comment: 'Возврат на $86 оформлен без подписи менеджера смены.'},
        {questionId: AQ_CASH_4, points: 1, yes: false},
    ]),
    makeRun('audr18-0000-0000-0000-0000000000ed', AUD_CASH_DISCIPLINE, LOC_AIRPORT, '2026-06-26T18:00:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_CASH_1, points: 2, yes: true},
        {questionId: AQ_CASH_2, points: 2, yes: true},
        {questionId: AQ_CASH_3, points: 2, yes: true},
        {questionId: AQ_CASH_4, points: 1, yes: false},
    ]),
    makeRun('audr19-0000-0000-0000-0000000000ee', AUD_CASH_DISCIPLINE, LOC_RIVERSIDE, '2026-06-27T16:45:00.000Z', CLIENT_USER_ID_2, [
        {questionId: AQ_CASH_1, points: 2, yes: true},
        {questionId: AQ_CASH_2, points: 2, yes: true},
        {questionId: AQ_CASH_3, points: 2, yes: true},
        {questionId: AQ_CASH_4, points: 1, yes: true},
    ]),
    makeRun('audr20-0000-0000-0000-0000000000ef', AUD_CASH_DISCIPLINE, LOC_SOUTHGATE, '2026-06-28T17:30:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_CASH_1, points: 2, yes: true},
        {questionId: AQ_CASH_2, points: 2, yes: false, comment: 'Сверка на начало смены проведена с опозданием, без письменного акта.'},
        {questionId: AQ_CASH_3, points: 2, yes: true},
        {questionId: AQ_CASH_4, points: 1, yes: true},
    ]),

    // Пожарная безопасность
    makeRun('audr21-0000-0000-0000-0000000000f0', AUD_FIRE_SAFETY, LOC_DOWNTOWN, '2026-06-05T09:00:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_FIRE_1, points: 2, yes: true},
        {questionId: AQ_FIRE_2, points: 2, yes: true},
        {questionId: AQ_FIRE_3, points: 2, yes: true},
        {questionId: AQ_FIRE_4, points: 2, yes: true},
        {questionId: AQ_FIRE_5, points: 2, yes: true},
    ]),
    makeRun('audr22-0000-0000-0000-0000000000f1', AUD_FIRE_SAFETY, LOC_WESTFIELD, '2026-06-06T09:30:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_FIRE_1, points: 2, yes: true},
        {questionId: AQ_FIRE_2, points: 2, yes: true},
        {questionId: AQ_FIRE_3, points: 2, yes: true},
        {questionId: AQ_FIRE_4, points: 2, yes: true},
        {questionId: AQ_FIRE_5, points: 2, yes: false},
    ]),
    makeRun('audr23-0000-0000-0000-0000000000f2', AUD_FIRE_SAFETY, LOC_HARBOR, '2026-06-07T10:00:00.000Z', CLIENT_USER_ID_2, [
        {questionId: AQ_FIRE_1, points: 2, yes: true},
        {questionId: AQ_FIRE_2, points: 2, yes: false, comment: 'Огнетушитель у складского выхода просрочен (истёк в мае).'},
        {questionId: AQ_FIRE_3, points: 2, yes: true},
        {questionId: AQ_FIRE_4, points: 2, yes: true},
        {questionId: AQ_FIRE_5, points: 2, yes: false},
    ]),
    makeRun('audr24-0000-0000-0000-0000000000f3', AUD_FIRE_SAFETY, LOC_NORTHLOOP, '2026-06-08T09:15:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_FIRE_1, points: 2, yes: true},
        {questionId: AQ_FIRE_2, points: 2, yes: true},
        {questionId: AQ_FIRE_3, points: 2, yes: false},
        {questionId: AQ_FIRE_4, points: 2, yes: true},
        {questionId: AQ_FIRE_5, points: 2, yes: true},
    ]),
    makeRun('audr25-0000-0000-0000-0000000000f4', AUD_FIRE_SAFETY, LOC_RIVERSIDE, '2026-06-29T09:00:00.000Z', CLIENT_USER_ID, [
        {questionId: AQ_FIRE_1, points: 2, yes: true},
        {questionId: AQ_FIRE_2, points: 2, yes: true},
        {questionId: AQ_FIRE_3, points: 2, yes: true},
        {questionId: AQ_FIRE_4, points: 2, yes: true},
        {questionId: AQ_FIRE_5, points: 2, yes: true},
    ]),
    makeRun('audr26-0000-0000-0000-0000000000f5', AUD_FIRE_SAFETY, LOC_SOUTHGATE, '2026-06-30T10:30:00.000Z', CLIENT_USER_ID_2, [
        {questionId: AQ_FIRE_1, points: 2, yes: false, comment: 'Путь к запасному выходу у примерочных частично перекрыт стеллажом с товаром.'},
        {questionId: AQ_FIRE_2, points: 2, yes: true},
        {questionId: AQ_FIRE_3, points: 2, yes: true},
        {questionId: AQ_FIRE_4, points: 2, yes: false},
        {questionId: AQ_FIRE_5, points: 2, yes: true},
    ]),
];

export const auditRuns = [...baseAuditRuns, ...extraRunEntries.map(e => e.run)];
export const auditAnswers = [...baseAuditAnswers, ...extraRunEntries.flatMap(e => e.answers)];

// ── Audit assignments (Results tab "Assignment" column + Assigned audits tab) ────
// Standing owners for the recurring Кассовая дисциплина audit, one per location -
// independent of whether that location's last run above is complete, since a
// recurring assignment stays "assigned" for the next cycle rather than resolving
// once a run finishes. University District is intentionally left unassigned,
// pairing with its "never run yet" state (see extraRunEntries comment above).
function assignmentOf(
    id: string, auditTemplateId: string, locationId: string,
    assignee: {userId?: string; roleId?: string}, dueAt: string,
    status: 'assigned' | 'in_progress' | 'completed' | 'cancelled' = 'assigned',
) {
    return {
        __typename: 'AuditAssignment' as const,
        id, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        auditTemplateId, locationId,
        assignedUserId: assignee.userId ?? null,
        assignedRoleId: assignee.roleId ?? null,
        status,
        dueAt, note: null,
        activeAuditRunId: null, completedAuditRunId: null,
        startedAt: null, completedAt: null,
        version: 1, createdAt: AUDIT_NOW, updatedAt: AUDIT_NOW,
        createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID, deletedAt: null,
    };
}

// isAssignmentOverdue (AuditAssignmentDS) compares dueAt against the real
// wall-clock Date.now(), not this scenario's fictional AUDIT_NOW - so unlike
// every other seed date in this file, these can't just sit in the story's
// June 2026 window or they'll all silently drift into "overdue" together as
// real time passes. Only Downtown is meant to demo the overdue state; the
// rest carry a rolling future offset from *today* so the Assignments tab
// keeps showing a healthy mix instead of a wall of red badges.
const CASH_DISCIPLINE_ASSIGNMENT_OVERDUE = '2026-07-02T10:00:00.000Z';
const daysFromNow = (days: number) => new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();

// Statuses vary across the set (not all 'assigned') so the Assignments tab's
// Status column shows every label the app can produce, not just one of them.
export const auditAssignments = [
    assignmentOf('auda01-0000-0000-0000-0000000000f1', AUD_CASH_DISCIPLINE, LOC_DOWNTOWN,  {userId: CLIENT_USER_ID},   CASH_DISCIPLINE_ASSIGNMENT_OVERDUE),
    assignmentOf('auda02-0000-0000-0000-0000000000f2', AUD_CASH_DISCIPLINE, LOC_WESTFIELD, {userId: CLIENT_USER_ID_2}, daysFromNow(4), 'in_progress'),
    assignmentOf('auda03-0000-0000-0000-0000000000f3', AUD_CASH_DISCIPLINE, LOC_HARBOR,    {userId: CLIENT_USER_ID_2}, daysFromNow(-9), 'completed'),
    assignmentOf('auda04-0000-0000-0000-0000000000f4', AUD_CASH_DISCIPLINE, LOC_AIRPORT,   {userId: CLIENT_USER_ID},   daysFromNow(6)),
    assignmentOf('auda05-0000-0000-0000-0000000000f5', AUD_CASH_DISCIPLINE, LOC_NORTHLOOP, {userId: CLIENT_USER_ID_3}, daysFromNow(-3), 'cancelled'),
    assignmentOf('auda06-0000-0000-0000-0000000000f6', AUD_CASH_DISCIPLINE, LOC_RIVERSIDE, {userId: CLIENT_USER_ID_2}, daysFromNow(10)),
    assignmentOf('auda07-0000-0000-0000-0000000000f7', AUD_CASH_DISCIPLINE, LOC_SOUTHGATE, {roleId: ROLE_ID_STORE_MANAGER}, daysFromNow(14)),
    // Follow-up re-audit for the location the "Needs attention" widget already
    // flags as the worst score (Store Floor & Safety Audit · Harbor Point, see
    // extraRunEntries above) - open to any Floor Associate rather than one named
    // person, since it's whoever's on the floor there next who picks it up.
    assignmentOf('auda08-0000-0000-0000-0000000000f8', AUD_FLOOR, LOC_HARBOR, {roleId: ROLE_ID_FLOOR_ASSOCIATE}, daysFromNow(5)),
];

// ── Issues (inbox) ───────────────────────────────────────────────────────────────
function issue(fields: Partial<Record<string, any>> & {id: string; source: string; title: string}) {
    const created = fields.createdAt ?? '2026-06-20T08:00:00.000Z';
    return {
        __typename: 'Issue' as const,
        ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        status: 'open',
        description: null, images: null,
        taskListUnitId: null, locationId: null, taskId: null, taskReportId: null,
        auditRunId: null, sourceRef: null, reportedBy: null,
        overdueCount: 0, lastSignalAt: created,
        resolutionType: null, correctiveTaskId: null, resolutionNote: null, dismissComment: null,
        resolvedBy: null, resolvedAt: null, archivedAt: null,
        version: 1, createdAt: created, updatedAt: created,
        createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID, deletedAt: null,
        ...fields,
    };
}

export const issues = [
    // Overdue — task missed its window, raised by cron (we only seed the row).
    issue({
        id: 'iss001-0000-0000-0000-0000000000e1',
        source: 'overdue', status: 'open',
        title: 'Cash count and safe drop (leave $300 float)',
        description: 'Evening closing task has been missed 3 days running at Downtown Flagship.',
        taskListUnitId: TLU_DAILY, taskId: TASK_CASH_DROP, locationId: LOC_DOWNTOWN,
        overdueCount: 3,
        createdAt: '2026-06-24T23:30:00.000Z', lastSignalAt: '2026-06-26T23:30:00.000Z',
    }),
    // Missed — recurring task's scheduled day passed with no report and no late-close
    // grace window, so it hardened straight to a terminal 'skipped' report (see the
    // matching rows in retail-chain.ts) instead of the still-closeable 'overdue' state
    // above. Raised by the same hardening cron, one issue per missed occurrence rather
    // than aggregated like overdue — we only seed the rows.
    issue({
        id: 'iss009-0000-0000-0000-0000000000e9',
        source: 'missed', status: 'open',
        title: 'Photo report: restocked shelves after pull',
        description: 'No report was submitted for the scheduled Jul 1 occurrence.',
        taskListUnitId: TLU_INVENTORY, taskId: TASK_PHOTO_SHELVES, taskReportId: 'rpt004-0000-0000-0000-000000000094',
        createdAt: '2026-07-01T12:00:00.000Z', lastSignalAt: '2026-07-01T12:00:00.000Z',
    }),
    issue({
        id: 'iss010-0000-0000-0000-0000000000ea',
        source: 'missed', status: 'open',
        title: 'Pull flagged stock from backroom and shelve correctly',
        description: 'No report was submitted for the scheduled Jul 3 occurrence.',
        taskListUnitId: TLU_INVENTORY, taskId: TASK_PULL_STOCK, taskReportId: 'rpt011-0000-0000-0000-00000000009b',
        createdAt: '2026-07-03T12:00:00.000Z', lastSignalAt: '2026-07-03T12:00:00.000Z',
    }),
    // Can't-complete flag — staff marked the task incomplete with a reason.
    issue({
        id: 'iss002-0000-0000-0000-0000000000e2',
        source: 'cant_complete', status: 'open',
        title: 'Photo report: stockroom tidied for next morning',
        description: 'Delivery arrived late and the stockroom still has unshelved cartons — can\'t photograph a tidy room.',
        taskListUnitId: TLU_DAILY, taskId: TASK_PHOTO_STOCKROOM, locationId: LOC_WESTFIELD,
        reportedBy: CLIENT_USER_ID_2,
        createdAt: '2026-06-25T21:10:00.000Z', lastSignalAt: '2026-06-25T21:10:00.000Z',
    }),
    // Manual report — filed by the default persona, shows in "My reports".
    issue({
        id: 'iss003-0000-0000-0000-0000000000e3',
        source: 'manual_report', status: 'open',
        title: 'Broken light fixture above register 2',
        description: 'Flickering and buzzing since this morning. Maintenance needs to look before it fails completely.',
        locationId: LOC_WESTFIELD, reportedBy: CLIENT_USER_ID,
        createdAt: '2026-06-26T09:20:00.000Z', lastSignalAt: '2026-06-26T09:20:00.000Z',
    }),
    // Audit finding — auto-raised by the failing Harbor Point run.
    issue({
        id: 'iss004-0000-0000-0000-0000000000e4',
        source: 'audit_finding', status: 'open',
        title: 'Price tickets missing or incorrect on several shelves',
        description: 'Failed question "Are all price tickets visible and correct?" — several tags didn\'t match the register price during the floor walk.',
        locationId: LOC_HARBOR, auditRunId: RUN_HARBOR,
        sourceRef: {percentage: 30, requiredPassPercentage: 80, score: 15, maxScore: 50},
        createdAt: '2026-06-18T10:15:00.000Z', lastSignalAt: '2026-06-18T10:15:00.000Z',
    }),
    // Audit finding — manually raised by an admin reviewing the Cash Discipline result
    // (not auto-raised; this scenario is what "Create issue" on a specific question
    // produces). Two of them, on two different failed questions of the same run —
    // demonstrates the location result page's "N issues created" counter, in contrast
    // to Harbor Point above which deliberately stays at exactly one.
    issue({
        id: 'iss007-0000-0000-0000-0000000000e7',
        source: 'audit_finding', status: 'open',
        title: 'Сверка наличности при открытии смены не проведена',
        description: 'Кассир не смог показать акт сверки на начало смены — только словесное подтверждение.',
        locationId: LOC_WESTFIELD, auditRunId: RUN_CASH_DISCIPLINE,
        sourceRef: {questionId: AQ_CASH_2},
        reportedBy: CLIENT_USER_ID_2,
        createdAt: '2026-06-23T17:45:00.000Z', lastSignalAt: '2026-06-23T17:45:00.000Z',
    }),
    issue({
        id: 'iss008-0000-0000-0000-0000000000e8',
        source: 'audit_finding', status: 'open',
        title: 'Возвраты оформлены без подписи менеджера',
        description: 'Два возврата за смену — оба без подписи ответственного менеджера на чеке.',
        locationId: LOC_WESTFIELD, auditRunId: RUN_CASH_DISCIPLINE,
        sourceRef: {questionId: AQ_CASH_3},
        reportedBy: CLIENT_USER_ID_2,
        createdAt: '2026-06-23T17:50:00.000Z', lastSignalAt: '2026-06-23T17:50:00.000Z',
    }),
    // Resolved with a corrective task — appears in resolved + "My reports".
    issue({
        id: 'iss005-0000-0000-0000-0000000000e5',
        source: 'manual_report', status: 'resolved',
        title: 'Spill near entrance left without a wet-floor sign',
        description: 'Customer nearly slipped. No sign was out.',
        locationId: LOC_DOWNTOWN, reportedBy: CLIENT_USER_ID,
        resolutionType: 'corrective_task', correctiveTaskId: TASK_ENTRANCE_SIGN,
        resolvedBy: CLIENT_USER_ID, resolvedAt: '2026-06-22T12:00:00.000Z',
        createdAt: '2026-06-21T16:40:00.000Z', lastSignalAt: '2026-06-21T16:40:00.000Z',
    }),
    // Audit finding resolved with a corrective task — the Harbor Point run also
    // flagged mislabelled stock; the fix is the recurring "Walk all sections"
    // task in retail-chain.ts (which links back via linkedIssueId).
    issue({
        id: 'iss011-0000-0000-0000-0000000000eb',
        source: 'audit_finding', status: 'resolved',
        title: 'Mislabelled stock found in several aisles',
        description: 'Shelf labels did not match the products on them in aisles 3 and 5 during the Harbor Point floor walk.',
        locationId: LOC_HARBOR, auditRunId: RUN_HARBOR,
        resolutionType: 'corrective_task', correctiveTaskId: TASK_SHELF_AUDIT,
        resolvedBy: CLIENT_USER_ID, resolvedAt: '2026-06-19T09:00:00.000Z',
        createdAt: '2026-06-18T10:20:00.000Z', lastSignalAt: '2026-06-18T10:20:00.000Z',
    }),
    // Dismissed — duplicate, shows the dismissed state + comment.
    issue({
        id: 'iss006-0000-0000-0000-0000000000e6',
        source: 'cant_complete', status: 'dismissed',
        title: 'Could not complete floor walk — manager absent',
        description: 'Opening associate skipped the floor walk.',
        taskListUnitId: TLU_DAILY, locationId: LOC_AIRPORT, reportedBy: CLIENT_USER_ID_2,
        dismissComment: 'Duplicate of the Airport opening checklist follow-up — tracking there instead.',
        resolvedBy: CLIENT_USER_ID, resolvedAt: '2026-06-23T08:30:00.000Z',
        createdAt: '2026-06-23T07:55:00.000Z', lastSignalAt: '2026-06-23T07:55:00.000Z',
    }),
];
